import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { WidgetPanel } from './DashboardShared';
import { emergencyRequestsOf, catalogProductsOf, locationLabelOf } from '../../../data/store';

export const EmergencyModule = ({ org }) => {
  const metrics = useMemo(() => {
    const requests = emergencyRequestsOf(org.id);
    const products = catalogProductsOf(org.id);
    
    let openRequests = 0;
    const openList = [];
    const recentActivity = [...requests].sort((a,b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, 5);

    requests.forEach(r => {
      if (r.status === 'open' || r.status === 'partially_fulfilled') {
        openRequests++;
        openList.push(r);
      }
    });

    const getProductName = (productId) => {
       const p = products.find(x => x.id === productId);
       return p ? p.name : 'Unknown Product';
    };

    return { openRequests, openList, recentActivity, getProductName };
  }, [org.id]);

  if (metrics.openRequests === 0 && metrics.recentActivity.length === 0) {
     return null; // Don't show emergency module if no data exists
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-rose-600 border-b border-[var(--theme-tertiary)] pb-3 mt-4">
        <AlertTriangle size={16} />
        Emergency Requests
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WidgetPanel title="🚨 Open Emergency Requests" count={metrics.openRequests} className="!border-rose-300 bg-rose-50/50 shadow-sm">
          <ul className="divide-y divide-rose-100 mt-2">
            {metrics.openList.length === 0 && <li className="py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-rose-300">No open requests</li>}
            {metrics.openList.map((r, idx) => (
              <li key={idx} className="py-2.5 flex items-center justify-between text-xs">
                <div className="flex flex-col gap-0.5">
                  <span className="font-bold text-rose-900 pr-4">{metrics.getProductName(r.productId)}</span>
                  <span className="text-[10px] font-bold text-rose-500 uppercase tracking-widest">{locationLabelOf(r.requestingBranchId)}</span>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="font-bold text-rose-700 bg-rose-200 px-2 py-0.5 rounded">{r.requestedQty - (r.fulfilledQty || 0)} needed</span>
                </div>
              </li>
            ))}
          </ul>
        </WidgetPanel>

        <WidgetPanel title="Recent Emergency Activity">
          <ul className="divide-y divide-[var(--theme-tertiary)]">
            {metrics.recentActivity.map((r, idx) => (
              <li key={idx} className="py-2.5 flex items-center justify-between text-xs">
                <div className="flex flex-col gap-0.5">
                  <span className="font-bold text-[#0d0d0d]">{metrics.getProductName(r.productId)}</span>
                  <span className="text-[10px] font-medium text-slate-400">By {locationLabelOf(r.requestingBranchId)}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {r.status === 'fulfilled' ? (
                     <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                        <CheckCircle size={12} /> Fulfilled
                     </span>
                  ) : (
                     <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        <Clock size={12} /> {r.status.replace('_', ' ')}
                     </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </WidgetPanel>
      </div>
    </div>
  );
};

EmergencyModule.propTypes = {
  org: PropTypes.object.isRequired,
};
